// ConceptDetail.js
// Ce module gère l'affichage détaillé d'un concept sélectionné

import React, { useState } from 'react';
import { BookOpen, X, Tag, Network, FileText, Calendar, Leaf } from 'lucide-react';

import SmartPDFViewer from '../SmartPDFViewer/SmartPDFViewer.js';

// Dialog modal pour afficher le détail d'un concept avec le support SmartPDFViewer
const ConceptDetailDialog = ({ 
  concept, 
  isOpen, 
  onClose, 
  onSelectRelatedConcept,
  userId,
  competence,
  makeAuthenticatedRequest,
  onError,
  onSelectConcept // Nouvelle prop pour la sélection du quiz
}) => {
  // États pour le EnhancedPDFViewer
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [conceptPassages, setConceptPassages] = useState([]);
  
  if (!concept || !isOpen) return null;
  
  // Récupérer la description détaillée du concept si disponible
  const description = concept.description;
  
  // Fonction pour ouvrir le PDFViewer avec les passages du concept
  const openPDFWithConceptPassages = (document) => {
    try {
      // Utiliser à la fois concept et title pour la compatibilité
      const conceptTitle = concept.concept || concept.title;

      // Rechercher la description du concept dans le document
      const conceptDescriptions = document.analysis?.conceptDescriptions || [];
      
      const conceptDescription = conceptDescriptions.find(
        desc => desc.concept === conceptTitle || desc.title === conceptTitle
      );

      // Préparer les passages et pages
      const passagesText = conceptDescription?.extraits || '';
      const pagesText = conceptDescription?.pages || '';

      // Convertir les pages en tableau
      const pages = typeof pagesText === 'string' 
        ? pagesText.split(/[,\s]+/).map(p => parseInt(p, 10)).filter(p => !isNaN(p))
        : [];

      // Créer des passages pour le PDFViewer
      const relevantPassages = pages.length > 0 
        ? pages.map((page, index) => ({
            id: `passage-${conceptTitle}-${index}`,
            pageNumber: page,
            content: typeof passagesText === 'string' 
              ? passagesText 
              : passagesText[index] || `Passage du concept "${conceptTitle}"`,
            highlights: {
              concept: conceptTitle
            }
          }))
        : [{
            id: `passage-generic-${conceptTitle}`,
            pageNumber: 1,
            content: passagesText || `Concept "${conceptTitle}" présent dans ce document.`,
            highlights: {
              concept: conceptTitle
            }
          }];

      // Configuration du PDFViewer
      setConceptPassages(relevantPassages);
      setSelectedDocument(document);
      setPdfViewerOpen(true);

      // Log détaillé pour le débogage
      console.log('Ouverture PDF pour le concept', {
        conceptTitle,
        documentTitle: document.title,
        passagesCount: relevantPassages.length,
        passages: relevantPassages,
        pages: pages
      });

    } catch (err) {
      console.error("Erreur lors de l'ouverture du PDF:", err);
      if (onError) {
        onError(err, "Ouverture du PDF");
      }
    }
  };

  // Fonction pour fermer le PDF viewer
  const closePDFViewer = () => {
    setPdfViewerOpen(false);
    setSelectedDocument(null);
    setConceptPassages([]);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-500" />
              <h2 className="text-xl font-semibold">{concept.title}</h2>
            </div>
            {concept.category && (
              <span className="inline-flex text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full self-start ml-7">
                {concept.category}
              </span>
            )}
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto flex-grow">
          {/* Section de présentation du concept */}
          {description && (
            <div className="space-y-4">
              {/* Définition générale du concept */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-600" />
                  <span>Définition</span>
                </h3>
                <p className="text-gray-700">
                  {description.description || description.résumé || description.definition || "Aucune définition disponible"}
                </p>
              </div>
              
              {/* Contexte d'utilisation */}
              {description.resume && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                  <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                    <Tag className="h-4 w-4 text-emerald-600" />
                    <span>Résumé</span>
                  </h3>
                  <p className="text-gray-700">{description.resume}</p>
                </div>
              )}
              
              {/* Autres aspects du concept */}
              {(description.importance || description.examples) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  {description.importance && (
                    <div className="mb-3">
                      <h3 className="font-medium text-gray-800 mb-1">Importance</h3>
                      <p className="text-gray-700">{description.importance}</p>
                    </div>
                  )}
                  
                  {description.examples && (
                    <div className="mb-3">
                      <h3 className="font-medium text-gray-800 mb-1">Exemples</h3>
                      <p className="text-gray-700">{description.examples}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Concepts liés */}
              {description.relatedConcepts && description.relatedConcepts.length > 0 && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                    <Network className="h-4 w-4 text-purple-600" />
                    <span>Concepts liés</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {description.relatedConcepts.map((relatedConcept, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (onSelectRelatedConcept) {
                            onSelectRelatedConcept(relatedConcept);
                          }
                        }}
                        className="px-3 py-1 bg-white text-purple-600 border border-purple-200 hover:bg-purple-100 rounded-md text-sm flex items-center gap-1 transition-colors"
                      >
                        <Leaf className="h-3 w-3" />
                        {relatedConcept}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bouton pour sélectionner ce concept pour un quiz - AJOUT */}
          {onSelectConcept && (
            <div className="my-4">
              <button
                onClick={() => onSelectConcept(concept)}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm transition-colors flex items-center justify-center"
              >
                <span>Sélectionner ce concept pour un quiz</span>
              </button>
            </div>
          )}

          <h3 className="font-medium my-4">Documents associés à ce concept</h3>
          <div className="space-y-4">
            {concept.documents.map((doc) => (
              <div 
                key={doc.id || doc.documentId || Math.random().toString()}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {doc.metadata?.title || doc.title || "Document sans titre"}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {new Date(doc.metadata?.uploadedAt || doc.metadata?.createdAt || doc.createdAt || new Date()).toLocaleDateString()}
                      </span>
                    </div>
                    {(doc.analysis?.summary || doc.summary) && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                        {doc.analysis?.summary || doc.summary}
                      </p>
                    )}
                    
                    {/* Afficher les autres concepts associés au document */}
                    {(doc.analysis?.concepts || doc.concepts) && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {(doc.analysis?.concepts || doc.concepts).filter(c => {
                          const conceptTitle = typeof c === 'object' ? c.title : c;
                          return conceptTitle !== concept.title;
                        }).slice(0, 5).map((c, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onSelectRelatedConcept) {
                                onSelectRelatedConcept(typeof c === 'object' ? c.title : c);
                              }
                            }}
                            className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200"
                          >
                            {typeof c === 'object' ? c.title : c}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Bouton pour ouvrir le EnhancedPDFViewer */}
                  <button
                    onClick={() => openPDFWithConceptPassages(doc)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex-shrink-0"
                  >
                    <FileText className="h-4 w-4" />
                    Ouvrir PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
      
      {/* Utiliser le SmartPDFViewer avec les variables correctes */}
      {pdfViewerOpen && selectedDocument && (
        <SmartPDFViewer
          documentUrl={selectedDocument.fileUrl}
          initialPassages={conceptPassages}
          documentId={selectedDocument.id}
          userId={userId}
          competenceId={competence?.id}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
          onError={onError}
          onClose={closePDFViewer}
        />
      )}
    </div>
  );
};

export { ConceptDetailDialog };